import React, { type PropsWithChildren, memo, useCallback, useState } from "react";
import styles from "./Extra.module.scss";
import Extra from "./Extra";
import type { ExtraType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import searchStyles from "./SearchBar.module.scss";
import { scrollToTarget } from "../../../../../../../../common-deprecated/utils/scrollUtils";
import { CarConfigConfigureSection } from "../../../../../utils/types/SectionTypes";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../utils/labelUtils";

// The amount of minimum extras that should be available before view all is added
const MIN_EXTRA_LENGTH = 6;
// The amount after how many items extras should be hidden
const SHOW_AFTER = 4;

const Extras: React.FC<PropsWithChildren<{ extras: ExtraType[] }>> = ({ extras }) => {
    const [hideExtras, setHideExtras] = useState<boolean>(extras.length > MIN_EXTRA_LENGTH); // State to control hiding packs

    const [ariaShowMoreExtras, ariaShowLessExtras] = useCarConfigLabel([
        "carConfigA11yButtonShowMoreExtras",
        "carConfigA11yButtonShowLessExtras",
    ]);

    const toggleExtras = useCallback((): void => {
        setHideExtras(!hideExtras);
        if (!hideExtras) {
            scrollToTarget(CarConfigConfigureSection.Packs);
        }
    }, [hideExtras]);

    return extras.length > 0 ? (
        <>
            <div className={styles.extras} id="extras-wrapper">
                {extras.slice(0, hideExtras ? SHOW_AFTER : extras.length).map((extra, index) => (
                    <Extra
                        key={extra.id}
                        extra={extra}
                        firstShownExtra={extras.length > MIN_EXTRA_LENGTH && index === SHOW_AFTER}
                    />
                ))}
            </div>
            {extras.length > MIN_EXTRA_LENGTH && (
                <div className={searchStyles.viewall}>
                    <Button
                        variant="btn-toggle"
                        onClick={toggleExtras} // Call togglePacks directly
                        aria-expanded={!hideExtras}
                        aria-controls="extras-wrapper"
                        aria-label={hideExtras ? ariaShowMoreExtras : ariaShowLessExtras}
                    >
                        <CarConfigLabel label={hideExtras ? "carConfigExtrasShowAll" : "carConfigExtrasShowLess"} />
                        <div className="btn-toggle-icon">
                            <Icon variant={hideExtras ? "plus" : "minus"} aria-hidden="true" />
                        </div>
                    </Button>
                </div>
            )}
        </>
    ) : (
        <div className={searchStyles.notfound} aria-live="assertive" aria-atomic="true">
            <CarConfigLabel label="carConfigExtrasNotFound" />
        </div>
    );
};

export default memo(Extras);
